import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import { Users, Search, Eye, Phone, Mail, MapPin, Plus, Download, Sliders, X, FileText, Receipt, Edit } from 'lucide-react';

const SalesCustomerList = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);

  useEffect(() => {
    if (!dataFetched) {
      setDataFetched(true);
      fetchCustomers();
    }
  }, [dataFetched]);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm, statusFilter, typeFilter]);

  const fetchCustomers = async (retryCount = 0) => {
    try {
      const response = await axios.get('/admin/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers:', error);
      
      if (error.response?.status === 429 && retryCount < 1) {
        setTimeout(() => {
          fetchCustomers(retryCount + 1);
        }, 1000);
        return;
      }
      
      // Use fallback sample data
      const sampleCustomers = [
        { 
          id: 1, 
          name: 'ABC Corporation', 
          phone: '+91 9876543210', 
          email: 'contact@abc.com',
          address: '123 Business Street, Mumbai, MH 400001',
          gstNumber: 'GST123456789'
        },
        { 
          id: 2, 
          name: 'XYZ Enterprises', 
          phone: '+91 9876543211', 
          email: 'info@xyz.com',
          address: '456 Commerce Road, Delhi, DL 110001',
          gstNumber: 'GST987654321'
        },
        { 
          id: 3, 
          name: 'PQR Industries', 
          phone: '+91 9876543212', 
          email: 'sales@pqr.com',
          address: '789 Industrial Area, Bangalore, KA 560001',
          gstNumber: 'GST456789123'
        }
      ];
      
      setCustomers(sampleCustomers);
      toast('Using sample data - API connection failed', { icon: '⚠️' });
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    let filtered = [...customers];

    // Search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter (mock data for demo)
    if (statusFilter.length > 0) {
      filtered = filtered.filter(customer => {
        // Mock status logic - in real app this would come from customer data
        const isActive = Math.random() > 0.3; // 70% active
        const isNewThisMonth = Math.random() > 0.8; // 20% new this month
        const hasPendingPayments = Math.random() > 0.7; // 30% have pending payments
        
        if (statusFilter.includes('active') && isActive) return true;
        if (statusFilter.includes('new_month') && isNewThisMonth) return true;
        if (statusFilter.includes('pending_payments') && hasPendingPayments) return true;
        return statusFilter.length === 0;
      });
    }

    // Type filter (mock data for demo)
    if (typeFilter.length > 0) {
      filtered = filtered.filter(customer => {
        // Mock type logic - in real app this would come from customer data
        const isVIP = Math.random() > 0.8; // 20% VIP
        const isCommercial = Math.random() > 0.6; // 40% commercial
        const isResidential = !isCommercial; // 60% residential
        
        if (typeFilter.includes('vip') && isVIP) return true;
        if (typeFilter.includes('commercial') && isCommercial) return true;
        if (typeFilter.includes('residential') && isResidential) return true;
        return typeFilter.length === 0;
      });
    }

    setFilteredCustomers(filtered);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter([]);
    setTypeFilter([]);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleTypeFilterChange = (type) => {
    setTypeFilter(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Navigation functions for customer actions
  const handleCreateEstimate = (customer) => {
    const prefilledCustomer = {
      name: customer.name,
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      gstNumber: customer.gstNumber || ''
    };
    
    navigate('/sales/estimates', { 
      state: { prefilledCustomer } 
    });
  };

  const handleCreateInvoice = (customer) => {
    const prefilledCustomer = {
      name: customer.name,
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      gstNumber: customer.gstNumber || ''
    };
    
    navigate('/sales/invoices', { 
      state: { prefilledCustomer } 
    });
  };

  if (loading) {
    return <div className="max-w-7xl mx-auto px-4 py-8">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="flex">
        {/* Left Sidebar - Filters */}
        <div className={`${showFilters ? 'w-80' : 'w-0'} transition-all duration-300 overflow-hidden bg-white border-r border-slate-200 flex-shrink-0`}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Customer Filters</h2>
              <button
                onClick={() => setShowFilters(false)}
                className="p-1 hover:bg-slate-100 rounded-lg transition-colors lg:hidden"
              >
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>

            {/* Status Filter */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-slate-900 mb-3">Status</h3>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={statusFilter.includes('active')}
                    onChange={() => handleStatusFilterChange('active')}
                    className="w-4 h-4 text-[#0A5F38] border-slate-300 rounded focus:ring-[#C5A57D]"
                  />
                  <span className="ml-2 text-sm text-slate-700">Active Customers</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={statusFilter.includes('new_month')}
                    onChange={() => handleStatusFilterChange('new_month')}
                    className="w-4 h-4 text-[#0A5F38] border-slate-300 rounded focus:ring-[#C5A57D]"
                  />
                  <span className="ml-2 text-sm text-slate-700">New This Month</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={statusFilter.includes('pending_payments')}
                    onChange={() => handleStatusFilterChange('pending_payments')}
                    className="w-4 h-4 text-[#0A5F38] border-slate-300 rounded focus:ring-[#C5A57D]"
                  />
                  <span className="ml-2 text-sm text-slate-700">With Pending Payments</span>
                </label>
              </div>
            </div>

            {/* Tags Filter */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-slate-900 mb-3">Tags</h3>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={typeFilter.includes('vip')}
                    onChange={() => handleTypeFilterChange('vip')}
                    className="w-4 h-4 text-[#0A5F38] border-slate-300 rounded focus:ring-[#C5A57D]"
                  />
                  <span className="ml-2 text-sm text-slate-700">VIP Clients</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={typeFilter.includes('commercial')}
                    onChange={() => handleTypeFilterChange('commercial')}
                    className="w-4 h-4 text-[#0A5F38] border-slate-300 rounded focus:ring-[#C5A57D]"
                  />
                  <span className="ml-2 text-sm text-slate-700">Commercial</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={typeFilter.includes('residential')}
                    onChange={() => handleTypeFilterChange('residential')}
                    className="w-4 h-4 text-[#0A5F38] border-slate-300 rounded focus:ring-[#C5A57D]"
                  />
                  <span className="ml-2 text-sm text-slate-700">Residential</span>
                </label>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="space-y-2">
              <button
                onClick={clearAllFilters}
                className="w-full px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-white transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="p-6">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Customers</h1>
                  <p className="text-slate-600 mt-1">Manage your customer database</p>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden flex items-center px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-white transition-colors"
                  >
                    <Sliders className="h-4 w-4 mr-2" />
                    Filters
                  </button>
                  <button className="flex items-center px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-white transition-colors">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </button>
                  <button className="flex items-center px-4 py-2 bg-[#C5A57D] text-white text-sm rounded-lg hover:bg-[#B8985E] transition-colors">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Customer
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent"
                />
              </div>
            </div>

            {/* Customers Table */}
            {filteredCustomers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No customers found</h3>
                <p className="text-slate-600">
                  {searchTerm || statusFilter.length > 0 || typeFilter.length > 0
                    ? 'Try adjusting your search or filter criteria.' 
                    : 'No customers are available at the moment.'}
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Contact</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Last Order</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredCustomers.map((customer) => {
                        // Mock data for demo
                        const customerType = Math.random() > 0.6 ? 'Commercial' : 'Residential';
                        const lastOrderDate = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB');
                        
                        return (
                          <tr key={customer.id} className="hover:bg-white transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <Users className="h-5 w-5 text-[#0A5F38]" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-slate-900">{customer.name}</div>
                                  {customer.gstNumber && (
                                    <div className="text-sm text-slate-500">GST: {customer.gstNumber}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-slate-900">{customer.phone || 'N/A'}</div>
                              <div className="text-sm text-slate-500">{customer.email || 'N/A'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                customerType === 'Commercial' 
                                  ? 'bg-purple-100 text-purple-800' 
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {customerType}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                              {lastOrderDate}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <button 
                                  onClick={() => handleCreateEstimate(customer)}
                                  className="inline-flex items-center px-3 py-1 text-xs font-medium text-[#0A5F38] bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                                >
                                  <FileText className="h-3 w-3 mr-1" />
                                  Estimate
                                </button>
                                <button 
                                  onClick={() => handleCreateInvoice(customer)}
                                  className="inline-flex items-center px-3 py-1 text-xs font-medium text-green-600 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
                                >
                                  <Receipt className="h-3 w-3 mr-1" />
                                  Invoice
                                </button>
                                <button className="inline-flex items-center px-3 py-1 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                                  <Edit className="h-3 w-3 mr-1" />
                                  Edit
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesCustomerList;



